import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Loader2, Upload, X, ImagePlus, GalleryHorizontal } from 'lucide-react';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { optimizeImage } from '@/utils/imageOptimization';

interface ProviderGalleryProps {
  providerId: string;
  userId: string;
  isOwner: boolean;
  maxStorageBytes?: number;
  storageUsedBytes?: number;
}

const PROVIDER_MAX_STORAGE = 3221225472; // 3 GB

export const ProviderGallery = ({ providerId, userId, isOwner, maxStorageBytes, storageUsedBytes }: ProviderGalleryProps) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [caption, setCaption] = useState('');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<string | null>(null);

  const maxStorage = maxStorageBytes || PROVIDER_MAX_STORAGE;
  const usedStorage = storageUsedBytes || 0;

  const { data: galleryPhotos = [], isLoading } = useQuery({
    queryKey: ['provider-gallery', providerId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('provider_gallery')
        .select('*')
        .eq('provider_id', providerId)
        .order('sort_order', { ascending: true });
      if (error) throw error;
      return data;
    },
    enabled: !!providerId,
  });

  const uploadMutation = useMutation({
    mutationFn: async ({ file: rawFile, caption }: { file: File; caption: string }) => {
      // Optimize image before uploading to reduce storage usage and feed load time.
      const file = rawFile.type.startsWith('image/')
        ? await optimizeImage(rawFile, { preset: 'feed' })
        : rawFile;

      // Check storage limit (using optimized size)
      if (usedStorage + file.size > maxStorage) {
        throw new Error(t('provider.gallery.storageLimitReached', { defaultValue: 'Storage limit reached. You have used your 3 GB allocation.' }));
      }

      const fileExt = file.name.split('.').pop()?.toLowerCase() || 'jpg';
      const fileName = `${userId}/${Date.now()}.${fileExt}`;

      const { error: uploadError } = await supabase.storage
        .from('provider-gallery')
        .upload(fileName, file, {
          contentType: file.type,
          cacheControl: '3600',
        });

      if (uploadError) throw uploadError;

      const { data: { publicUrl } } = supabase.storage
        .from('provider-gallery')
        .getPublicUrl(fileName);

      const { error: insertError } = await supabase
        .from('provider_gallery')
        .insert({
          provider_id: providerId,
          user_id: userId,
          image_url: publicUrl,
          caption,
          size_bytes: file.size,
          sort_order: galleryPhotos.length,
        });

      if (insertError) throw insertError;

      // Update storage_used_bytes on provider
      await supabase
        .from('service_providers')
        .update({ storage_used_bytes: usedStorage + file.size })
        .eq('id', providerId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['provider-gallery', providerId] });
      queryClient.invalidateQueries({ queryKey: ['provider-profile'] });
      toast.success(t('provider.gallery.uploadSuccess', { defaultValue: 'Photo uploaded to gallery' }));
      setShowUploadModal(false);
      setSelectedFile(null);
      setPreview(null);
      setCaption('');
    },
    onError: (error: any) => {
      toast.error(error.message || t('provider.gallery.uploadError', { defaultValue: 'Failed to upload photo' }));
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (photo: any) => {
      // Delete from storage
      const url = new URL(photo.image_url);
      const path = url.pathname.split('/provider-gallery/')[1];
      if (path) {
        await supabase.storage.from('provider-gallery').remove([path]);
      }

      // Delete from DB
      const { error } = await supabase
        .from('provider_gallery')
        .delete()
        .eq('id', photo.id);
      if (error) throw error;

      // Update storage_used_bytes
      const newUsed = Math.max(0, usedStorage - (photo.size_bytes || 0));
      await supabase
        .from('service_providers')
        .update({ storage_used_bytes: newUsed })
        .eq('id', providerId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['provider-gallery', providerId] });
      queryClient.invalidateQueries({ queryKey: ['provider-profile'] });
      toast.success(t('provider.gallery.deleteSuccess', { defaultValue: 'Photo removed from gallery' }));
    },
    onError: () => {
      toast.error(t('provider.gallery.deleteError', { defaultValue: 'Failed to delete photo' }));
    },
  });

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      toast.error(t('provider.gallery.invalidType', { defaultValue: 'Only JPEG, PNG, and WebP images are allowed' }));
      return;
    }

    if (file.size > 52428800) {
      toast.error(t('provider.gallery.fileTooLarge', { defaultValue: 'File too large. Maximum size is 50MB' }));
      return;
    }

    setSelectedFile(file);
    setPreview(URL.createObjectURL(file));
  };

  const handleUpload = () => {
    if (!selectedFile) return;
    uploadMutation.mutate({ file: selectedFile, caption });
  };

  const storageUsedMB = Math.round(usedStorage / (1024 * 1024));
  const storageMaxMB = Math.round(maxStorage / (1024 * 1024));

  if (isLoading) {
    return (
      <Card>
        <CardContent className="pt-6 flex justify-center">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <GalleryHorizontal className="w-5 h-5 text-primary" />
                {t('provider.gallery.title', { defaultValue: 'Portfolio / Gallery' })}
              </CardTitle>
              <CardDescription>
                {t('provider.gallery.description', { defaultValue: 'Showcase your work to attract more customers' })}
                {isOwner && (
                  <span className="ml-2 text-xs">
                    ({storageUsedMB} MB / {storageMaxMB} MB)
                  </span>
                )}
              </CardDescription>
            </div>
            {isOwner && (
              <Button variant="outline" size="sm" onClick={() => setShowUploadModal(true)}>
                <ImagePlus className="w-4 h-4 mr-2" />
                {t('provider.gallery.addPhoto', { defaultValue: 'Add Photo' })}
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {galleryPhotos.length > 0 ? (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {galleryPhotos.map((photo: any) => (
                <div key={photo.id} className="relative group aspect-square">
                  <img
                    src={photo.image_url}
                    alt={photo.caption || 'Gallery photo'}
                    className="w-full h-full object-cover rounded-lg"
                    loading="lazy"
                  />
                  {photo.caption && (
                    <div className="absolute bottom-0 left-0 right-0 bg-black/60 text-white text-xs p-2 rounded-b-lg opacity-0 group-hover:opacity-100 transition-opacity">
                      {photo.caption}
                    </div>
                  )}
                  {isOwner && (
                    <button
                      onClick={() => deleteMutation.mutate(photo)}
                      disabled={deleteMutation.isPending}
                      className="absolute top-2 right-2 p-1.5 bg-destructive text-destructive-foreground rounded-full opacity-0 group-hover:opacity-100 transition-opacity hover:bg-destructive/90"
                    >
                      {deleteMutation.isPending ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <X className="w-4 h-4" />
                      )}
                    </button>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8">
              <GalleryHorizontal className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
              <p className="text-muted-foreground text-sm">
                {isOwner
                  ? t('provider.gallery.emptyOwner', { defaultValue: 'No gallery photos yet. Showcase your work!' })
                  : t('provider.gallery.emptyPublic', { defaultValue: 'No gallery photos available' })}
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Upload Modal */}
      <Dialog open={showUploadModal} onOpenChange={setShowUploadModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{t('provider.gallery.uploadTitle', { defaultValue: 'Add Gallery Photo' })}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {preview ? (
              <div className="relative aspect-video rounded-lg overflow-hidden">
                <img src={preview} alt="Preview" className="w-full h-full object-cover" />
                <button
                  onClick={() => { setSelectedFile(null); setPreview(null); }}
                  className="absolute top-2 right-2 p-1 bg-black/50 text-white rounded-full"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            ) : (
              <label className="flex flex-col items-center justify-center h-40 border-2 border-dashed rounded-lg cursor-pointer hover:border-primary">
                <Upload className="w-8 h-8 text-muted-foreground mb-2" />
                <span className="text-sm text-muted-foreground">{t('provider.gallery.selectPhoto', { defaultValue: 'Select a photo' })}</span>
                <input type="file" accept="image/jpeg,image/png,image/webp" onChange={handleFileSelect} className="hidden" />
              </label>
            )}
            <Input
              placeholder={t('provider.gallery.captionPlaceholder', { defaultValue: 'Add a caption (optional)' })}
              value={caption}
              onChange={(e) => setCaption(e.target.value)}
            />
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setShowUploadModal(false)}>
                {t('common.cancel')}
              </Button>
              <Button onClick={handleUpload} disabled={!selectedFile || uploadMutation.isPending}>
                {uploadMutation.isPending ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" />{t('common.processing')}</>
                ) : (
                  <><Upload className="w-4 h-4 mr-2" />{t('provider.gallery.upload', { defaultValue: 'Upload' })}</>
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};
